import React from 'react';
import { I18n as I } from 'react-redux-i18n';
import { ControlTypes, FieldInfo, useFilteredRegions } from 'vdlib';

const defaultCountryID = 67;

const defaultFormData = {
  // Auction Data
  CountryID: defaultCountryID,
  MarketValue: 0,
  ProposedMarketValue: 0,
  RegionID: null,

  // Folder Data
  GearBoxID: 1,
  FrameNumber: '',
  FuelTypeID: 2,
  Kilometers: '',
  Number: '',
  PlateNumber: '',
  VehicleBrand: '',
  VehicleDoors: '',
  VehicleEngine: '',
  VehicleEngineCapacity: '',
  VehicleModel: '',
  VehicleRegistrationYear: '',
  VehicleTypeID: 1,
  WheelDriveID: 0,
};

const defaultValidationMessages = Object.keys({
  ...defaultFormData,
}).reduce((obj, key) => {
  obj[key] = '';
  return obj;
}, {});

const formatCreationData = (
  {
    CountryID,
    GearBoxID,
    FrameNumber,
    FuelTypeID,
    Kilometers,
    MarketValue,
    Number,
    PlateNumber,
    ProposedMarketValue,
    RegionID,
    VehicleBrand,
    VehicleDoors,
    VehicleEngine,
    VehicleEngineCapacity,
    VehicleModel,
    VehicleRegistrationYear,
    VehicleTypeID,
    WheelDriveID,
  },
  auction
) => [
  {
    ID: auction ? auction.ID : 0,
    CountryID,
    MarketValue,
    ProposedMarketValue,
    RegionID,
  },
  {
    GearBoxID,
    FrameNumber,
    FuelTypeID,
    ID: auction ? auction.FolderID : 0,
    Kilometers,
    Number,
    PlateNumber,
    VehicleBrand,
    VehicleDoors,
    VehicleEngine,
    VehicleEngineCapacity,
    VehicleModel,
    VehicleRegistrationYear,
    VehicleTypeID,
    WheelDriveID,
  },
];

const useAuctionFields = (
  {
    auction,
    countries,
    fuelTypes,
    gearBoxes,
    lang,
    regions,
    vehicleTypes,
    wheelDrives,
    party,
  },
  validateFunc
) => {
  const [formData, setFormData] = React.useState({
    ...defaultFormData,
    CountryID: party?.CountryID || defaultCountryID,
  });
  const [validationMessages, setValidationMessages] = React.useState(
    defaultValidationMessages
  );

  React.useEffect(() => {
    if (auction)
      setFormData({
        ...auction,
        Number: auction.FolderNumber,
        FolderNumber: undefined,
      });
  }, [auction]);

  const formContext = React.useRef({
    updateForm: (name, value) =>
      setFormData(fd => {
        setValidationMessages(msgs => ({
          ...msgs,
          ...validateFunc(name, value, fields, fd),
        }));

        return { ...fd, [name]: value };
      }),
  });

  // filter regions based on selected country
  const filteredRegions = useFilteredRegions(
    regions,
    countries,
    formData.CountryID
  );

  // on filtered regions change, select the first region, if existing
  React.useEffect(() => {
    if (filteredRegions && filteredRegions.length)
      setFormData(fd => {
        const existing = fd.RegionID
          ? filteredRegions.find(r => r.ID === fd.RegionID)
          : null;
        return existing
          ? fd
          : {
              ...fd,
              RegionID: filteredRegions[0].ID,
            };
      });
  }, [filteredRegions]); //eslint-disable-line

  const fields = React.useMemo(
    () =>
      [
        new FieldInfo('Number', I.t('FOLDER_NO'), ''),
        new FieldInfo('PlateNumber', I.t('PLATE_NUMBER'), ''),
        new FieldInfo('VehicleBrand', I.t('BRAND'), ''),
        new FieldInfo('VehicleModel', I.t('MODEL'), ''),
        new FieldInfo('FrameNumber', I.t('FRAME_NUMBER'), ''),
        new FieldInfo('VehicleRegistrationYear', I.t('REGISTRATION_YEAR'), ''),
        new FieldInfo(
          'VehicleTypeID',
          I.t('VEHICLE_TYPE'),
          1,
          vehicleTypes,
          null,
          ControlTypes.Dropdown
        ),
        new FieldInfo('VehicleDoors', I.t('DOORS'), ''),
        new FieldInfo('VehicleEngine', I.t('ENGINE_TYPE'), ''),
        new FieldInfo('VehicleEngineCapacity', I.t('ENGINE_CAPACITY'), ''),
        new FieldInfo(
          'FuelTypeID',
          I.t('FUEL_TYPE'),
          2,
          fuelTypes,
          null,
          ControlTypes.Dropdown
        ),
        new FieldInfo(
          'GearBoxID',
          I.t('GEARBOX'),
          1,
          gearBoxes,
          null,
          ControlTypes.Dropdown
        ),
        new FieldInfo(
          'WheelDriveID',
          I.t('WHEELDRIVE'),
          1,
          wheelDrives,
          null,
          ControlTypes.Dropdown
        ),
        new FieldInfo('Kilometers', I.t('KILOMETERS'), ''),
        new FieldInfo(
          'MarketValue',
          I.t('MARKET_VALUE'),
          0,
          null,
          null,
          ControlTypes.Currency
        ),
        new FieldInfo(
          'ProposedMarketValue',
          I.t('PROPOSED_MARKET_VALUE'),
          0,
          null,
          null,
          ControlTypes.Currency
        ),
        new FieldInfo(
          'CountryID',
          I.t('COUNTRY'),
          party?.CountryID || defaultCountryID,
          countries,
          null,
          ControlTypes.Dropdown
        ),
        new FieldInfo(
          'RegionID',
          I.t('REGION'),
          52,
          filteredRegions,
          null,
          ControlTypes.Dropdown
        ),
      ].map(f => Object.assign(f, { formContext: formContext.current })),
    [
      countries,
      fuelTypes,
      gearBoxes,
      filteredRegions,
      vehicleTypes,
      wheelDrives,
      lang,
      party,
    ]
  );

  return {
    fields,
    formData,
    setFormData,
    setValidationMessages,
    validationMessages,
  };
};

export {
  defaultFormData,
  defaultValidationMessages,
  formatCreationData,
  useAuctionFields,
};
